import { useState } from "react";
import { GetProfileWeeklySummaryQuery } from "types/graphql-schema";

import { pluralize } from "@helpers/string";

import SummarySourceChip from "./summary-source-chip";

type SummarySource = NonNullable<
  GetProfileWeeklySummaryQuery["profileWeeklySummary"]
>["insights"][number]["sources"][number];

// Activity shows chips inline, capped so a busy line stays readable.
const MAX_ACTIVITY_CHIPS = 4;

const TOGGLE_CLASS =
  "w-fit text-xs font-medium text-gray-400 hover:text-gray-600";

// Collapse sources that render as the same chip (e.g. several "Stand up"
// meetings) so a row isn't cluttered with visually-identical pills.
const dedupeByLabel = (
  sources: readonly SummarySource[],
): readonly SummarySource[] => {
  const seen = new Set<string>();
  const result: SummarySource[] = [];
  for (const source of sources) {
    const key = (source.title || source.type).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(source);
  }
  return result;
};

// Renders a summary line's sources. Insights keep only a muted "N sources" count
// (the observation is the point) that reveals the chips on click; activity shows
// slimmed chips inline, capped with a "+N more" expander.
const SummaryLineSources = ({
  sources,
  variant,
}: {
  sources: readonly SummarySource[];
  variant: "insight" | "activity";
}) => {
  const [expanded, setExpanded] = useState(false);
  const deduped = dedupeByLabel(sources);
  if (deduped.length === 0) return null;

  if (variant === "insight") {
    return (
      <div className="flex flex-col gap-1.5">
        <button
          className={TOGGLE_CLASS}
          onClick={() => setExpanded((value) => !value)}
        >
          {deduped.length} {pluralize("source", deduped.length)}
        </button>
        {expanded && (
          <div className="flex flex-wrap gap-1.5">
            {deduped.map((source) => (
              <SummarySourceChip key={source.id} source={source} />
            ))}
          </div>
        )}
      </div>
    );
  }

  const visible = expanded ? deduped : deduped.slice(0, MAX_ACTIVITY_CHIPS);
  const hiddenCount = deduped.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((source) => (
        <SummarySourceChip key={source.id} source={source} />
      ))}
      {hiddenCount > 0 && (
        <button className={TOGGLE_CLASS} onClick={() => setExpanded(true)}>
          +{hiddenCount} more
        </button>
      )}
    </div>
  );
};

export default SummaryLineSources;
